import { apiCall, ApiResult } from './core';
import { projectAPI } from './projectApi';

// Client API exports
export const getClients = projectAPI.getClients;
export const createClient = projectAPI.createClient;

// Dashboard API
// Helper to get selected client ID from localStorage (for partner/aws users)
const getSelectedClientId = (): string | null => {
  try {
    return localStorage.getItem('selectedClientId');
  } catch {
    return null;
  }
};

// Helper to append client_id to endpoint
const appendClientId = (endpoint: string, clientId: string | null = null): string => {
  const selectedClientId = clientId || getSelectedClientId();
  if (!selectedClientId) return endpoint;

  const separator = endpoint.includes('?') ? '&' : '?';
  return `${endpoint}${separator}client_id=${selectedClientId}`;
};

export const dashboardAPI = {
  // Get all dashboards
  getDashboards: async (clientId: string | null = null, includeUnpublished = false): Promise<ApiResult> => {
    try {
      let endpoint = '/dashboards';
      const params = new URLSearchParams();
      const selectedClientId = clientId || getSelectedClientId();
      if (selectedClientId) {
        params.append('client_id', selectedClientId);
      }
      if (includeUnpublished) {
        params.append('include_unpublished', 'true');
      }
      if (params.toString()) {
        endpoint += `?${params.toString()}`;
      }
      const data = await apiCall(endpoint);
      return { success: true, data: data || [] };
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      console.error('Error fetching dashboards:', error);
      return { success: false, error: err.message };
    }
  },

  // Get published dashboards
  getPublishedDashboards: async (clientId: string | null = null): Promise<ApiResult> => {
    try {
      const endpoint = appendClientId('/dashboards/published', clientId);
      const data = await apiCall(endpoint);
      return { success: true, data: data || [] };
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      console.error('Error fetching published dashboards:', error);
      return { success: false, error: err.message };
    }
  },

  // Get default dashboard, optionally by context
  getDefaultDashboard: async (clientId: string | null = null, context: string | null = null): Promise<ApiResult & { notFound?: boolean }> => {
    try {
      let endpoint = appendClientId('/dashboards/default', clientId);
      // Add context parameter if provided
      if (context) {
        const separator = endpoint.includes('?') ? '&' : '?';
        endpoint += `${separator}context=${context}`;
      }
      // 404 is expected when no default dashboard exists, so silence it
      const data = await apiCall(endpoint, { silentErrors: [404] });
      return { success: true, data };
    } catch (error) {
      const err = error as { status?: number; message?: string };
      // 404 is expected when no default dashboard exists for context
      if (err.status === 404 || err.message?.includes('No default dashboard found')) {
        return { success: false, error: err.message, notFound: true };
      }
      console.error('Error fetching default dashboard:', error);
      return { success: false, error: err.message };
    }
  },

  // Get dashboard by ID
  getDashboard: async (dashboardId: string): Promise<ApiResult> => {
    try {
      const data = await apiCall(`/dashboards/${dashboardId}`);
      return { success: true, data };
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      console.error('Error fetching dashboard:', error);
      return { success: false, error: err.message };
    }
  },

  // Create dashboard
  createDashboard: async (dashboardData: Record<string, any>, clientId: string | null = null): Promise<ApiResult> => {
    try {
      const endpoint = appendClientId('/dashboards', clientId);
      const data = await apiCall(endpoint, {
        method: 'POST',
        body: JSON.stringify(dashboardData)
      });
      return { success: true, data };
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      console.error('Error creating dashboard:', error);
      return { success: false, error: err.message };
    }
  },

  // Update dashboard
  updateDashboard: async (dashboardId: string, updates: Record<string, any>): Promise<ApiResult> => {
    try {
      const data = await apiCall(`/dashboards/${dashboardId}`, {
        method: 'PUT',
        body: JSON.stringify(updates)
      });
      return { success: true, data };
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      console.error('Error updating dashboard:', error);
      return { success: false, error: err.message };
    }
  },

  // Delete dashboard
  deleteDashboard: async (dashboardId: string): Promise<ApiResult> => {
    try {
      await apiCall(`/dashboards/${dashboardId}`, {
        method: 'DELETE'
      });
      return { success: true };
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      console.error('Error deleting dashboard:', error);
      return { success: false, error: err.message };
    }
  },

  // Publish dashboard
  publishDashboard: async (dashboardId: string): Promise<ApiResult> => {
    try {
      const data = await apiCall(`/dashboards/${dashboardId}/publish`, {
        method: 'POST'
      });
      return { success: true, data };
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      console.error('Error publishing dashboard:', error);
      return { success: false, error: err.message };
    }
  },

  // Unpublish dashboard (uses update endpoint to set is_published=false)
  unpublishDashboard: async (dashboardId: string): Promise<ApiResult> => {
    try {
      const data = await apiCall(`/dashboards/${dashboardId}`, {
        method: 'PUT',
        body: JSON.stringify({ is_published: false })
      });
      return { success: true, data };
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      console.error('Error unpublishing dashboard:', error);
      return { success: false, error: err.message };
    }
  },

  // Set dashboard as default (legacy - for custom dashboards only)
  setDefaultDashboard: async (dashboardId: string, clientId: string | null = null): Promise<ApiResult> => {
    try {
      const endpoint = appendClientId(`/dashboards/${dashboardId}/set-default`, clientId);
      const data = await apiCall(endpoint, {
        method: 'POST'
      });
      return { success: true, data };
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      console.error('Error setting default dashboard:', error);
      return { success: false, error: err.message };
    }
  },

  // Set default dashboard preference (system template or custom dashboard)
  setDefaultPreference: async (defaultType: string, defaultKey: string, context: string, clientId: string | null = null): Promise<ApiResult> => {
    try {
      const endpoint = appendClientId('/dashboards/default', clientId);
      const data = await apiCall(endpoint, {
        method: 'PUT',
        body: JSON.stringify({
          default_type: defaultType,
          default_key: defaultKey,
          context: context,
        }),
      });
      return { success: true, data };
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      console.error('Error setting default preference:', error);
      return { success: false, error: err.message };
    }
  },
};
